import axios from 'axios';

/**
 * fetchGroups
 *
 * Retrieves a player's groups from the server.
 *
 * @param player
 * @returns {Promise<*>}
 */
export const fetchGroups = async (player) => {
  let queryString = '';
  if (player.email) {
    queryString = 'playerId=' + encodeURIComponent(player._id);
  } else {
    queryString = 'tempPlayerId=' + encodeURIComponent(player.temp_id);
  }

  const response = await get('/getGroups?' + queryString);
  if (response.data.error || !response.data.groups) {
    console.error('Failed to fetch groups: ', response.data.error);
    return response.data;
  }

  return response.data.groups;
}

/**
 * removePlayerFromGroup
 *
 * Removes a player from a group.
 *
 * @param player
 * @param groupGuid
 * @returns {Promise<void>}
 */
export const removePlayerFromGroup = async (player, groupGuid) => {
  const payload = {
    groupGuid: groupGuid,
    playerId: player._id ?? player.temp_id
  };
  const response = await post('/removePlayerFromGroup', payload);
  return response.data;
}

/**
 * deactivateGroup
 *
 * Deactivates a group.
 *
 * @param groupGuid
 * @returns {Promise<axios.AxiosResponse<*>|{data: {error}}>}
 */
export const deactivateGroup = async (groupGuid) => {
  const payload = {
    groupGuid: groupGuid
  };
  const response = await post('/deactivateGroup', payload);
  return response.data;
}

/**
 * fetchSubjects
 *
 * Retrieves subjects from the server.
 *
 * @returns {Promise<Array>|null}
 */
export const fetchSubjects = async () => {
  try {
    console.log('process.env.REACT_APP_API_URL: ', process.env.REACT_APP_API_URL);
    const response = await get('/getSubjects');
    if (!response.data.success && response.data.error) {
      console.error('Failed to fetch subjects: ', response.data.error);
      return null;
    }
    return response.data.subjects;
  } catch (err) {
    console.error('Failed to fetch subjects: ', err);
    return null;
  }
}

/**
 * newSubject
 *
 * Creates a new subject on the server.
 *
 * @param subject
 * @param playerId
 * @returns {Promise<*>}
 */
export const newSubject = async (subject, playerId) => {
  const response = post('/newSubject', { subject: subject, playerId: playerId });
  return response.data;
}

/**
 * createGroup
 *
 * Creates a new group on the server.
 * If the player from local storage doesn't exist it will be created on the server as well.
 *
 * @param subjectId
 * @returns {Promise<object|null>}
 */
export const createGroup = async (player, subjectId) => {
  const createDt = new Date();
  createDt.setHours(0, 0, 0, 0);

  const response = await post('/addGroup', {
    subjectId: subjectId,
    player: player,
    localDate: createDt
  });

  if (response.data.error) {
    console.error('Failed to create new group: ', response.data.error);
    return null;
  }

  return response.data;
}

/**
 * fetchGroup
 *
 * Retrieves a group from the server.
 *
 * @param groupGuid
 * @param player
 * @returns {Promise<object|null>}
 */
export const fetchGroup = async (groupGuid, player = null) => {
  let query = '?groupGuid=' + groupGuid;
  if (player && (player._id || player.temp_id)) {
    query += '&playerId=' + player._id || player.temp_id;
  }
  const response = await get('/getGroup' + query);

  if (response.data.error) {
    console.error('Failed to create new group: ', response.data.error);
    return null;
  }

  return response.data;
}

/**
 * fetchQuestion
 *
 * Retrieves a new question from the server.
 *
 * @param subjectId
 * @param groupGuid
 * @param player
 * @returns {Promise<*|null>}
 */
export const fetchQuestion = async (subjectId, groupGuid, player = null) => {
  let query = '?subjectId=' + subjectId + '&groupGuid=' + groupGuid;

  if (player && (player._id || player.temp_id)) {
    query += '&playerId=' + (player.email ? player._id : player.temp_id);
  }

  const response = await get('/getQuestion' + query);
  if (response.data.error || !response.data.question) {
    console.error('Failed to get question: ', response.data.error);
    return null;
  }

  return response.data.question;
}

/**
 * flagQuestion
 *
 * Flags a question.
 *
 * @param questionId
 * @returns {Promise<T|{error}|*|null>}
 */
export const flagQuestion = async (questionId) => {
  const response = await post('/flagQuestion', {
    question_id: questionId
  });

  if (response.data.error) {
    console.error('Failed to flag question: ', response.data.error);
    return null;
  }

  return response.data;
}

/**
 * sendAnswer
 *
 * Sends an answer to the server.
 *
 * @param groupGuid
 * @param questionId
 * @param answer (see useEffect)
 * @returns {Promise<boolean>}
 */
export const sendAnswer = async (groupGuid, questionId, answer) => {
  const response = await post('/addAnswer', {
    groupGuid: groupGuid,
    questionId: questionId,
    answer: answer
  });
  if (response.data.error) {
    console.error('Failed to add answer: ', response.data.error);
  }

  return response.data;
}

/**
 * addPlayer
 *
 * Adds a player to the server.
 *
 * @param newPlayer
 * @returns {Promise<object|null>}
 */
export const addPlayer = async (newPlayer) => {
  const response = await post('/newPlayer', { player: newPlayer });

  if (response.data.success === false || !response.data.player) {
    if (response.data.error) {
      setErrorMsg(response.data.error);
    } else {
      setErrorMsg('Unknown error. Please try again.');
    }
    return null;
  }

  return response.data.player;
}

/**
 * fetchPlayer
 *
 * @param playerId
 * @returns {Promise<object|null>}
 */
export const fetchPlayer = async (playerId) => {
  const response = await get('/getPlayer?id=' + playerId);
  if (response.data.error) {
    return null;
  }
  return response.data.player;
}

/**
 * updatePlayer
 *
 * @param player
 * @returns {Promise<void>}
 */
export const updatePlayer = async (player) => {
  const response = await post('/updatePlayer', { player: player });
  return response.data;
}

/**
 * login
 *
 * @param email
 * @param password
 * @returns {Promise<*|null>}
 */
export const login = async (email, password) => {
  const response = await post('/login', {
    email: email,
    password: password
  });

  if (response.data.error) {
    console.error('Failed to login: ', response.data.error);
    return null;
  }

  return response.data;
}

/**
 * addPlayerToGroup
 *
 * @param playerId
 * @param groupGuid
 * @returns {Promise<object|null>}
 */
export const addPlayerToGroup = async (playerId, groupGuid) => {
  const response = await post('/addPlayerToGroup', {
    playerId: playerId,
    groupGuid: groupGuid
  });

  if (response.data.error) {
    console.error('Failed to add player to group: ', response.data.error);
    return null;
  }

  return response.data;
}

/**
 * fetchAdminData
 *
 * Retrieves admin stat data from the server.
 *
 * @returns {Promise<T | {error} | *>}
 */
export const fetchAdminData = async () => {
  const response = await get('/getAdminData');
  if (response.status !== 200 && response.data.success === false ) {
    console.log(response.data.message);
    return;
  }

  return response.data;
}

/**
 * post
 *
 * Wrapper for axios.post to the server. Adds common headers and logs errors.
 *
 * @param url
 * @param payload
 * @returns {Promise<axios.AxiosResponse<any>|{data: {error}}>}
 */
const post = async (url, payload) => {
  try {
    const response = await axios.post(process.env.REACT_APP_API_URL + url, payload, {
      headers: { 'Authorization': 'Bearer ' + process.env.REACT_APP_API_ACCESS_TOKEN }
    });

    return response;
  } catch (e) {
    console.error('Failed to post to ' + url + ': ', e);
    return { data: { error: e } };
  }
}

/**
 * get
 *
 * Wrapper for axios.get to the server. Adds common headers and logs errors.
 *
 * @param url
 * @returns {Promise<axios.AxiosResponse<any>|{data: {error}}>}
 */
const get = async (url) => {
  try {
    return await axios.get(process.env.REACT_APP_API_URL + url, {
      headers: { 'Authorization': 'Bearer ' + process.env.REACT_APP_API_ACCESS_TOKEN }
    });
  } catch (e) {
    console.error('Failed to get ' + url + ': ', e);
    return { data: { error: e } };
  }
}
